import { Env } from '@env';
import * as WebBrowser from 'expo-web-browser';
import { useEffect, useRef, useState } from 'react';
import { Linking, Platform, ScrollView, View } from 'react-native';
import Modal from 'react-native-modal';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import type { Brand } from '@/api/cars/types';
import { useCustomer } from '@/api/customers/use-customer';
import { useBrands } from '@/api/partners/brands/use-brands';
import {
  NavigationButton,
  NavigationButtonTypes,
} from '@/components/navigation-button';
import mixpanelService from '@/core/clients/mixpanel';
import { Button, Text } from '@/ui';
import Alert, { AlertType } from '@/ui/alert';
import { Icon } from '@/ui/icon';
import { Info } from '@/ui/icons';
import { Loader } from '@/ui/loader';
import { Tooltip } from '@/ui/tooltip';

import { CardBrand } from '../card-brand';

interface AddCarsModalProps {
  visible?: boolean;
  onClose: () => void;
  style?: any;
  redirectPath?: string;
}

export function AddCarsModal({
  visible = false,
  onClose,
  style,
  redirectPath,
}: AddCarsModalProps) {
  const { styles, theme } = useStyles(stylesheet);
  const [showModal, setShowModal] = useState(visible);
  const [activeBrand, setActiveBrand] = useState<Brand | null>(null);
  const didSetDefaultBrand = useRef(false);

  const { data: customer } = useCustomer();

  const {
    data: brandsData,
    isPending: isFetchingBrands,
    refetch: refetchBrands,
  } = useBrands({});

  useEffect(() => {
    setShowModal(visible);
  }, [visible]);

  useEffect(() => {
    if (!didSetDefaultBrand.current && brandsData?.length) {
      const tesla = brandsData.find(
        (brand: Brand) => brand.displayName === 'Tesla'
      );
      if (tesla) {
        didSetDefaultBrand.current = true;
        setActiveBrand(tesla);
      }
    }
  }, [brandsData]);

  useEffect(() => {
    if (Platform.OS !== 'web' && redirectPath) {
      const subscription = Linking.addEventListener('url', (event) => {
        if (event.url.includes(redirectPath)) {
          setShowModal(false);
          onClose?.();
        }
      });
      return () => subscription.remove();
    }
  }, [onClose, redirectPath]);

  const handleConnectCars = async () => {
    let baseUrl = `${Env.STANDARD_FLEET_URI}&partner_ref=${customer?.partnerToken}`;

    if (redirectPath) {
      const redirectUri =
        Platform.OS === 'web'
          ? `${window.location.origin}/${redirectPath}`
          : `eon://${redirectPath}`;

      baseUrl += `&redirect_uri=${redirectUri}`;
    }

    mixpanelService.track('Connecting SF Started');

    if (Platform.OS === 'web') {
      window.open(baseUrl, '_blank');
    } else {
      await WebBrowser.openBrowserAsync(baseUrl);
    }
  };

  return (
    <Modal
      animationIn="slideInUp"
      animationInTiming={300}
      animationOut="slideOutDown"
      animationOutTiming={300}
      isVisible={showModal}
      hasBackdrop
      style={{
        ...styles.container,
        ...style,
      }}
      onBackdropPress={() => {
        setShowModal(false);
        onClose?.();
      }}
    >
      <View style={styles.containerView}>
        <View
          style={{
            flexDirection: 'row',
            paddingLeft: 16,
            paddingTop: 16,
            paddingRight: 16,
          }}
        >
          <View>
            <Text type="title2">Add Cars</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Choose a car brand you'd like to add.
            </Text>
          </View>

          <NavigationButton
            type={NavigationButtonTypes.cancel}
            onPress={() => {
              setShowModal(false);
              onClose?.();
            }}
            style={{
              position: 'absolute',
              right: 16,
              top: 16,
            }}
          />
        </View>

        <View style={styles.brandsAlertContainer}>
          <ScrollView
            style={styles.brandsScroll}
            contentContainerStyle={styles.brandsContainer}
          >
            {isFetchingBrands ? <Loader /> : null}

            {brandsData?.length > 0
              ? brandsData.map((brand: Brand) => {
                  return (
                    <CardBrand
                      brand={brand}
                      key={brand.name}
                      onPress={() => {
                        if (activeBrand?.name === brand.name) {
                          setActiveBrand(null);
                        } else {
                          setActiveBrand(brand);
                        }
                      }}
                      active={activeBrand?.name === brand.name}
                      style={{ flex: 1 }}
                      imageStyle={{ height: 40 }}
                    />
                  );
                })
              : null}
          </ScrollView>

          {activeBrand && activeBrand?.displayName !== 'Tesla' ? (
            <Alert
              type={AlertType.WARNING}
              title="Please contact us"
              body="To list your car please contact support@eonride.com. Limited features currently available (no digital key, GPS, or toll tracking). Full integration coming soon."
            />
          ) : null}
        </View>

        <View
          style={{
            padding: 16,
            paddingTop: 0,
            gap: 24,
          }}
        >
          {activeBrand?.displayName === 'Tesla' ? (
            <View style={styles.beforeConnectContainer}>
              <View style={styles.beforeConnectHeaderRow}>
                <Text type="bodyBold">Before you connect</Text>

                <Tooltip text="Connecting a car to Eon takes owner-level access. Sign in with the account that owns the car, not a driver account it was shared to">
                  <View style={styles.whatIfRow}>
                    <Icon
                      type="naked"
                      icon={<Info />}
                      fillColor={theme.colors.icon.color}
                      width={15}
                      height={15}
                    />

                    <Text type="caption1" style={styles.whatIfText}>
                      What if I manage cars that were shared to my account?
                    </Text>
                  </View>
                </Tooltip>
              </View>

              <Text
                type="bodyRegular"
                style={[
                  { color: theme.colors.text.secondary },
                  styles.sharedCarsText,
                ]}
              >
                If the cars were shared to you by their owner, you'll need
                owner-level access to connect them fully.
              </Text>
            </View>
          ) : null}

          <View style={styles.bottomButtonsContainer}>
            <Button
              text="Next"
              type="primary"
              onPress={handleConnectCars}
              style={styles.button}
              disabled={activeBrand?.displayName !== 'Tesla'}
            />

            <Button
              text="Cancel"
              type="ghost"
              onPress={() => {
                setShowModal(false);
                onClose?.();
              }}
              style={styles.button}
            />
          </View>
        </View>
      </View>
    </Modal>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    margin: 0,
    marginTop: 50,
    justifyContent: {
      xs: 'flex-end',
      md: 'center',
    },
    alignSelf: 'center',
    maxWidth: {
      xs: '100%',
      sm: 600,
    },
    width: {
      xs: '100%',
    },
  },
  containerView: {
    backgroundColor: theme.colors.background_2,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    gap: 16,
    borderRadius: 16,
    borderBottomLeftRadius: {
      xs: 0,
      md: 16,
    },
    borderBottomRightRadius: {
      xs: 0,
      md: 16,
    },
    height: {
      xs: '100%',
      md: 'auto',
    },
  },
  button: {
    width: {
      xs: '100%',
      md: 150,
    },
  },
  brandsAlertContainer: {
    flex: 1,
    padding: 16,
    gap: 16,
    flexDirection: 'column',
  },
  brandsScroll: {
    flex: 1,
  },
  brandsContainer: {
    gap: 16,
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
  },
  bottomButtonsContainer: {
    flexDirection: {
      xs: 'column',
      md: 'row-reverse',
    },
    gap: 4,
    alignItems: 'flex-end',
  },
  beforeConnectContainer: {
    gap: 4,
  },
  beforeConnectHeaderRow: {
    flexDirection: {
      xs: 'column',
      md: 'row',
    },
    alignItems: {
      xs: 'stretch',
      md: 'center',
    },
    gap: 8,
  },
  sharedCarsText: {
    display: {
      xs: 'none',
      md: 'flex',
    },
  },
  whatIfRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    width: '100%',
  },
  whatIfText: {
    color: theme.colors.text.secondary,
    textDecorationLine: 'underline',
  },
}));
